import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col, Card, InputGroup, Alert } from 'react-bootstrap';
import { useForm, SubmitHandler } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRocket, 
  faHome, 
  faDollarSign, 
  faPercent, 
  faCalendar,
  faUser,
  faBuilding,
  faTimes,
  faSave,
  faStar,
  faMapMarkerAlt,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import ReactSelect from 'components/base/ReactSelect';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import { useAtom } from 'jotai';
import { dealsAtom } from 'jotai/LeadsJotaiInstance';
import { getISODate } from 'helpers/utils';

// Type definitions based on your database schema
type DealFormData = {
  deal_name: string;
  deal_email?: string;
  deal_commission: number;
  deal_commission_type: 'flat' | 'percentage';
  deal_price: number;
  deal_price_type?: string;
  deal_closing_date?: string;
  deal_city?: string;
  deal_state?: string;
  deal_zip?: string;
  deal_note?: string;
  deal_stage_id: number;
  deal_inspection_period?: number;
  lead_type_id: number;
  lead_type_status_id: number;
  property_address?: string;
  property_mls_id?: string;
  property_city?: string;
  property_state?: string;
  property_zip?: string;
  customer_lead_ids?: string[];
};

interface DealModalProps {
  show: boolean;
  onHide: () => void;
  stageId?: number;
  dealId?: string; // For edit mode
  mode?: 'create' | 'edit';
}

const DealModal: React.FC<DealModalProps> = ({ show, onHide, stageId = 1, dealId, mode = 'create' }) => {
  const [deals, setDeals] = useAtom(dealsAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadTypes, setLeadTypes] = useState<any[]>([]);
  const [leadStatuses, setLeadStatuses] = useState<any[]>([]);
  const [dealStages, setDealStages] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [leadsData, setLeadsData] = useState<any[]>([]);
  const [collaborators, setCollaborators] = useState<any[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<DealFormData>({
    defaultValues: {
      deal_commission_type: 'percentage',
      deal_stage_id: stageId,
      deal_price_type: 'sale',
      customer_lead_ids: []
    }
  });

  const watchCommissionType = watch('deal_commission_type');
  const watchPrice = watch('deal_price');
  const watchCommission = watch('deal_commission');
  const watchLeadTypeId = watch('lead_type_id');
  const watchPropertyAddress = watch('property_address');

  // Calculate commission amount for display
  const calculateCommissionAmount = () => {
    if (!watchPrice || !watchCommission) return 0;
    if (watchCommissionType === 'percentage') {
      return (watchPrice * watchCommission) / 100;
    }
    return watchCommission;
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadTypesRes, dealStagesRes, leadsRes, collaboratorsRes] = await Promise.all([
          axiosInstance.get('/leads/types'),
          axiosInstance.get('/deals'),
          axiosInstance.get('/leads'),
          axiosInstance.get('/collaborators')
        ]);

        console.log('🔍 API Responses:', {
          leadTypes: leadTypesRes.data,
          dealStages: dealStagesRes.data,
          leads: leadsRes.data,
          collaborators: collaboratorsRes.data
        });

        setLeadTypes(leadTypesRes.data?.data?.leadTypes || []);
        setDealStages(dealStagesRes.data?.data || []);
        
        // Transform leads data to match expected format
        const leadsArray = leadsRes.data?.data?.leads || [];
        setLeadsData(leadsArray); // Store full lead data
        const transformedLeads = leadsArray.map((lead: any) => {
          const name = `${lead.lead_first_name || ''} ${lead.lead_last_name || ''}`.trim();
          const email = lead.lead_email ? ` - ${lead.lead_email}` : '';
          const phone = lead.lead_phone ? ` - ${lead.lead_phone}` : '';
          const location = lead.lead_city ? ` - ${lead.lead_city}, ${lead.lead_state || ''}` : '';
          
          return {
            label: `${name}${email}${phone}${location}`,
            value: lead.id,
            data: lead // Store full lead data for reference
          };
        });
        setLeads(transformedLeads);
        
        // Transform collaborators data to match expected format
        const transformedCollaborators = collaboratorsRes.data?.data?.collaborators?.map((collaborator: any) => ({
          label: collaborator.invitee.collaborator_first_name,
          value: collaborator.collaborator_id
        })) || [];
        setCollaborators(transformedCollaborators);

        console.log('🔍 Processed Data:', {
          leadTypes: leadTypesRes.data?.data?.leadTypes || [],
          dealStages: dealStagesRes.data?.data || [],
          leads: transformedLeads,
          collaborators: transformedCollaborators
        });
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Failed to load form data');
      }
    };

    if (show) {
      fetchData();
    }
  }, [show]);

  // Load deal data for edit mode
  useEffect(() => {
    const loadDealData = async () => {
      if (mode === 'edit' && dealId && show) {
        try {
          const response = await axiosInstance.get(`/deals/${dealId}`);
          const dealData = response.data?.data?.deal || response.data?.deal;

          if (!dealData) {
            toast.error('Deal data not found');
            return;
          }

          // Populate form with existing deal data
          setValue('deal_name', dealData.deal_name || '');
          setValue('deal_email', dealData.deal_email || '');
          setValue('deal_commission', dealData.deal_commission || 0);
          setValue('deal_commission_type', dealData.deal_commission_type || 'percentage');
          setValue('deal_price', dealData.deal_price || 0);
          setValue('deal_price_type', dealData.deal_price_type || 'sale');
          setValue('deal_closing_date', dealData.deal_closing_date?.split('T')[0] || '');
          setValue('deal_city', dealData.deal_city || '');
          setValue('deal_state', dealData.deal_state || '');
          setValue('deal_zip', dealData.deal_zip || '');
          setValue('deal_note', dealData.deal_note || '');
          setValue('deal_stage_id', dealData.deal_stage_id || stageId);
          setValue('deal_inspection_period', dealData.deal_inspection_period || 0);
          setValue('lead_type_id', dealData.lead_type_id || 0);
          setValue('lead_type_status_id', dealData.lead_type_status_id || 0);
          setValue('property_address', dealData.property_address || '');
          setValue('property_mls_id', dealData.property_mls_id || '');
          setValue('property_city', dealData.property_city || '');
          setValue('property_state', dealData.property_state || '');
          setValue('property_zip', dealData.property_zip || '');
          
          // Set customers if deal has customers
          if (dealData.customers && dealData.customers.length > 0) {
            const customerIds = dealData.customers.map((customer: any) => 
              customer.lead_id || customer.id || ''
            ).filter(Boolean);
            setValue('customer_lead_ids', customerIds);
          }

          console.log('📝 Loaded deal data for editing:', dealData);
        } catch (error) {
          console.error('Error loading deal data:', error);
          toast.error('Failed to load deal data');
        }
      }
    };

    loadDealData();
  }, [mode, dealId, show, setValue, stageId]);

  // Fetch lead statuses when lead type changes
  useEffect(() => {
    const fetchLeadStatuses = async () => {
      if (watchLeadTypeId) {
        try {
          const response = await axiosInstance.get(`/leads/status/${watchLeadTypeId}`);
          setLeadStatuses(response.data?.data?.leadTypes || []);
        } catch (error) {
          console.error('Error fetching lead statuses:', error);
        }
      }
    };

    fetchLeadStatuses();
  }, [watchLeadTypeId]);

  // Debug property address changes
  useEffect(() => {
    console.log('🏠 Property address watch value changed:', watchPropertyAddress);
  }, [watchPropertyAddress]);

  const onSubmit: SubmitHandler<DealFormData> = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Construct payload matching your database schema exactly
      const payload = {
        deal_name: data.deal_name.trim(),
        deal_email: data.deal_email?.trim() || null,
        deal_commission: Number(data.deal_commission),
        deal_commission_type: data.deal_commission_type,
        deal_price: Number(data.deal_price),
        deal_price_type: data.deal_price_type || 'sale',
        deal_closing_date: data.deal_closing_date ? getISODate(new Date(data.deal_closing_date)) : null,
        deal_city: data.deal_city?.trim() || null,
        deal_state: data.deal_state?.trim() || null,
        deal_zip: data.deal_zip?.trim() || null,
        deal_note: data.deal_note?.trim() || null,
        deal_stage_id: Number(data.deal_stage_id),
        deal_inspection_period: data.deal_inspection_period ? Number(data.deal_inspection_period) : null,
        lead_type_id: Number(data.lead_type_id),
        lead_type_status_id: Number(data.lead_type_status_id),
        // Property fields - the key focus
        property_address: data.property_address?.trim() || null,
        property_mls_id: data.property_mls_id?.trim() || null,
        property_city: data.property_city?.trim() || null,
        property_state: data.property_state?.trim() || null,
        property_zip: data.property_zip?.trim() || null,
        // Additional fields
        customers: data.customer_lead_ids?.map(lead_id => ({ lead_id })) || [],
        collaborators: [],
        related_deals: []
      };

      console.log(`🚀 ${mode === 'edit' ? 'Updating' : 'Creating'} deal with payload:`, payload);
      console.log('🏠 Property address:', payload.property_address);
      console.log('🔍 Form data received:', data);
      console.log('🔍 Raw property_address from form:', data.property_address);
      console.log('🔍 Property address length:', data.property_address?.length);
      console.log('🔍 Property address type:', typeof data.property_address);

      const response = mode === 'edit' 
        ? await axiosInstance.patch(`/deals/${dealId}`, payload)
        : await axiosInstance.post('/deals', payload);
      
      if (response.data.success !== false) {
        toast.success(`🎉 Deal ${mode === 'edit' ? 'updated' : 'created'} successfully!`);
        
        // Update global state with complete deals data
        console.log('🔄 Updating deals state with response data:', response.data.data);
        console.log('🔄 Is response.data.data an array?', Array.isArray(response.data.data));
        
        if (Array.isArray(response.data.data)) {
          (setDeals as any)(response.data.data);
        } else {
          console.warn('⚠️ Response data is not an array, keeping existing deals state');
          toast('Deal saved but display may not update immediately', { icon: '⚠️' });
        }
        
        // Reset form and close modal
        reset();
        onHide();
      } else {
        throw new Error(response.data.message || `Failed to ${mode} deal`);
      }
    } catch (error: any) {
      console.error(`❌ Deal ${mode} failed:`, error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${mode} deal`;
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleCancel}
      size="xl"
      backdrop="static"
      keyboard={false}
      className="ai-modal"
    >
      <Modal.Header className="border-0 pb-0">
        <div className="d-flex align-items-center">
          <div 
            className="rounded-circle me-3 d-flex align-items-center justify-content-center"
            style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: '48px',
              height: '48px'
            }}
          >
            <FontAwesomeIcon icon={faRocket} className="text-white" size="lg" />
          </div>
          <div>
            <h4 className="mb-1 fw-bold text-primary">{mode === 'edit' ? 'Edit Deal' : 'Create New Deal'}</h4>
            <p className="mb-0 text-muted">
              <FontAwesomeIcon icon={faStar} className="me-1" />
              AI-Powered Deal Intelligence Ready
            </p>
          </div>
        </div>
        <Button 
          variant="outline-secondary" 
          size="sm"
          onClick={handleCancel}
          className="rounded-circle p-2"
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="g-4">
            {/* Deal Information Section */}
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-primary bg-opacity-10 border-0">
                  <h6 className="mb-0 text-primary fw-semibold">
                    <FontAwesomeIcon icon={faIdCard} className="me-2" />
                    Deal Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Deal Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          {...register('deal_name', { 
                            required: 'Deal name is required',
                            minLength: { value: 3, message: 'Deal name must be at least 3 characters' }
                          })}
                          type="text"
                          placeholder="e.g., 1027 S Lola Lane, Tempe, AZ"
                          className="border-2"
                          isInvalid={!!errors.deal_name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.deal_name?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Deal Email</Form.Label>
                        <Form.Control
                          {...register('deal_email', {
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address'
                            }
                          })}
                          type="email"
                          placeholder="deal@example.com"
                          className="border-2"
                          isInvalid={!!errors.deal_email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.deal_email?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Customer Selection Row */}
                  <Row className="g-3 mt-2">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          <FontAwesomeIcon icon={faUser} className="me-1" />
                          Attach to Existing Customers/Leads
                        </Form.Label>
                        <ReactSelect
                          options={leads}
                          placeholder="Search and select customers (e.g., husband & wife)"
                          isClearable
                          isMulti
                          isSearchable
                          value={leads.filter(lead => watch('customer_lead_ids')?.includes(lead.value)) || []}
                          onChange={(selected: any) => {
                            const selectedArray = Array.isArray(selected) ? selected : [];
                            const selectedIds = selectedArray.map(s => s.value) || [];
                            setValue('customer_lead_ids', selectedIds);
                            
                            // Auto-fill email from first selected lead if no email set
                            if (selectedArray.length > 0 && !watch('deal_email')) {
                              const firstSelectedLead = leadsData?.find((l: any) => l.id === selectedArray[0].value);
                              if (firstSelectedLead?.lead_email) {
                                setValue('deal_email', firstSelectedLead.lead_email);
                              }
                            }
                          }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderWidth: '2px',
                              '&:hover': {
                                borderColor: '#6366f1'
                              }
                            })
                          }}
                        />
                        <Form.Text className="text-muted">
                          Select multiple customers for this deal (e.g., couples, business partners)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Property Information Section */}
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-success bg-opacity-10 border-0">
                  <h6 className="mb-0 text-success fw-semibold">
                    <FontAwesomeIcon icon={faHome} className="me-2" />
                    Property Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Property Address
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                          </InputGroup.Text>
                          <Form.Control
                            {...register('property_address')}
                            type="text"
                            placeholder="Enter property address if known"
                            className="border-2"
                          />
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Optional - You can add this later when you have the property details
                        </Form.Text>
                        {/* Debug button - remove after testing */}
                        <Button 
                          type="button" 
                          variant="outline-info" 
                          size="sm" 
                          className="mt-2"
                          onClick={() => {
                            const currentValues = watch();
                            console.log('🔍 Current form values:', currentValues);
                            console.log('🔍 Property address specifically:', currentValues.property_address);
                          }}
                        >
                          Debug Form Values
                        </Button>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">City</Form.Label>
                        <Form.Control
                          {...register('property_city')}
                          type="text"
                          placeholder="Enter city"
                          className="border-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">State</Form.Label>
                        <Form.Control
                          {...register('property_state')}
                          type="text"
                          placeholder="Enter state"
                          className="border-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">ZIP Code</Form.Label>
                        <Form.Control
                          {...register('property_zip')}
                          type="text"
                          placeholder="Enter ZIP"
                          className="border-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Property MLS ID</Form.Label>
                        <Form.Control
                          {...register('property_mls_id')}
                          type="text"
                          placeholder="Property MLS ID"
                          className="border-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Financial Information Section */}
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-warning bg-opacity-10 border-0">
                  <h6 className="mb-0 text-warning fw-semibold">
                    <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                    Financial Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Deal Price <span className="text-danger">*</span>
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            {...register('deal_price', { 
                              required: 'Deal price is required',
                              min: { value: 1, message: 'Price must be greater than 0' }
                            })}
                            type="number"
                            placeholder="409000"
                            className="border-2"
                            isInvalid={!!errors.deal_price}
                          />
                        </InputGroup>
                        <Form.Control.Feedback type="invalid">
                          {errors.deal_price?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Commission</Form.Label>
                        <Row className="g-2">
                          <Col xs={4}>
                            <Form.Select 
                              {...register('deal_commission_type')}
                              className="border-2"
                            >
                              <option value="percentage">%</option>
                              <option value="flat">$</option>
                            </Form.Select>
                          </Col>
                          <Col xs={8}>
                            <InputGroup>
                              <Form.Control
                                {...register('deal_commission', {
                                  min: { value: 0, message: 'Commission cannot be negative' }
                                })}
                                type="number"
                                step="0.01"
                                placeholder="2.5"
                                className="border-2"
                                isInvalid={!!errors.deal_commission}
                              />
                              <InputGroup.Text>
                                <FontAwesomeIcon icon={faPercent} />
                              </InputGroup.Text>
                            </InputGroup>
                            <Form.Control.Feedback type="invalid">
                              {errors.deal_commission?.message}
                            </Form.Control.Feedback>
                          </Col>
                        </Row>
                        {watchPrice && watchCommission && (
                          <Form.Text className="text-success fw-semibold">
                            Commission Amount: ${calculateCommissionAmount().toLocaleString()}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Deal Details Section */}
            <Col xs={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-info bg-opacity-10 border-0">
                  <h6 className="mb-0 text-info fw-semibold">
                    <FontAwesomeIcon icon={faBuilding} className="me-2" />
                    Deal Details
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Lead Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select 
                          {...register('lead_type_id', { required: 'Lead type is required' })}
                          className="border-2"
                          isInvalid={!!errors.lead_type_id}
                        >
                          <option value="">Select Lead Type</option>
                          {leadTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.lead_type}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.lead_type_id?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Status <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select 
                          {...register('lead_type_status_id', { required: 'Status is required' })}
                          className="border-2"
                          isInvalid={!!errors.lead_type_status_id}
                        >
                          <option value="">Select Status</option>
                          {leadStatuses.map((status) => (
                            <option key={status.id} value={status.id}>
                              {status.status}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.lead_type_status_id?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Deal Stage <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select 
                          {...register('deal_stage_id', { required: 'Deal stage is required' })}
                          className="border-2"
                          isInvalid={!!errors.deal_stage_id}
                        >
                          <option value="">Select Stage</option>
                          {dealStages.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.deal_stage_name}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.deal_stage_id?.message}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Closing Date</Form.Label>
                        <InputGroup>
                          <InputGroup.Text>
                            <FontAwesomeIcon icon={faCalendar} />
                          </InputGroup.Text>
                          <Form.Control
                            {...register('deal_closing_date')}
                            type="date"
                            className="border-2"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Inspection Period (days)</Form.Label>
                        <Form.Control
                          {...register('deal_inspection_period')}
                          type="number"
                          placeholder="10"
                          className="border-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Deal Notes</Form.Label>
                        <Form.Control
                          {...register('deal_note')}
                          as="textarea"
                          rows={3}
                          placeholder="Add any additional notes about this deal..."
                          className="border-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row className="mt-4">
            <Col xs={12}>
              <div className="d-flex justify-content-end gap-3">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={isSubmitting}
                  className="px-4"
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {mode === 'edit' ? 'Updating Deal...' : 'Creating Deal...'}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-2" />
                      {mode === 'edit' ? 'Update Deal' : 'Create Deal'}
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <style>{`
        .ai-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        
        .ai-modal .modal-header {
          padding: 1.5rem 1.5rem 0.5rem 1.5rem;
        }
        
        .ai-modal .card {
          border-radius: 12px;
          transition: transform 0.2s ease;
        }
        
        .ai-modal .card:hover {
          transform: translateY(-2px);
        }
        
        .ai-modal .card-header {
          border-radius: 12px 12px 0 0;
        }
        
        .ai-modal .form-control,
        .ai-modal .form-select {
          border-radius: 8px;
          transition: all 0.2s ease;
        }
        
        .ai-modal .form-control:focus,
        .ai-modal .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }
      `}</style>
    </Modal>
  );
};

export default DealModal;